import type { CSSProperties, ReactNode } from "react";
import Image from "next/image";
import { Layers3 } from "lucide-react";
import { cn, getPokemonSetIconUrl } from "utils/presentation";
import type { ShortCardSet } from "modules/common/model/models";
import { RiftboundIcon } from "modules/common/components/Icons/RiftboundIcon";
import { PokemonIcon } from "modules/common/components/Icons/PokemonIcon";
import { OnePieceIcon } from "modules/common/components/Icons/OnePieceIcon";
import { getOnePieceDisplaySetCode } from "modules/cards/utils/onepieceSetCode";

export interface GameIconRenderProps {
  size: number;
  color: string;
  className?: string;
}

export type GameIconRenderer = (props: GameIconRenderProps) => ReactNode;

interface BrandImageSpec {
  src: string;
  intrinsicWidth: number;
  intrinsicHeight: number;
}

function renderBrandImage(
  props: GameIconRenderProps,
  { src, intrinsicWidth, intrinsicHeight }: BrandImageSpec,
  widthRatio = 1
) {
  return (
    <Image
      src={src}
      alt=""
      width={intrinsicWidth}
      height={intrinsicHeight}
      className={cn("shrink-0 object-contain", props.className)}
      style={{ width: props.size * widthRatio, height: props.size }}
      draggable={false}
      aria-hidden="true"
    />
  );
}

export const renderPlaneswalkerGameIcon: GameIconRenderer = ({
  size,
  color,
  className,
}) => (
  <span
    className={cn("inline-block shrink-0 transition-colors", className)}
    style={{
      width: size,
      height: size,
      backgroundColor: color,
      maskImage: 'url("/img/gameIcons/planeswalkerLogo.svg")',
      maskPosition: "center",
      maskRepeat: "no-repeat",
      maskSize: "contain",
      WebkitMaskImage: 'url("/img/gameIcons/planeswalkerLogo.svg")',
      WebkitMaskPosition: "center",
      WebkitMaskRepeat: "no-repeat",
      WebkitMaskSize: "contain",
    }}
    aria-hidden="true"
  />
);

export const renderRiftboundGameIcon: GameIconRenderer = (props) => (
  <RiftboundIcon
    fontSize={props.size}
    color={props.color}
    className={props.className}
  />
);

export const renderPokemonGameIcon: GameIconRenderer = (props) => (
  <PokemonIcon
    fontSize={props.size}
    color={props.color}
    className={props.className}
  />
);

export const renderOnePieceGameIcon: GameIconRenderer = (props) => (
  <OnePieceIcon
    fontSize={props.size}
    color={props.color}
    className={props.className}
  />
);

export const renderStarWarsUnlimitedGameIcon: GameIconRenderer = (props) =>
  renderBrandImage(
    props,
    {
      src: "/img/gameIcons/starWarsUnlimitedIcon.png",
      intrinsicWidth: 680,
      intrinsicHeight: 285,
    },
    1.5
  );

export const renderStarWarsUnlimitedBrandMark: GameIconRenderer = (props) =>
  renderBrandImage(props, {
    src: "/img/gameIcons/starWarsUnlimitedLogo.png",
    intrinsicWidth: 700,
    intrinsicHeight: 501,
  });

export const renderLorcanaGameIcon: GameIconRenderer = (props) =>
  renderBrandImage(props, {
    src: "/img/gameIcons/lorcanaMark.png",
    intrinsicWidth: 192,
    intrinsicHeight: 192,
  });

export const renderFusionWorldGameIcon: GameIconRenderer = ({
  size,
  className,
}) => (
  <svg
    viewBox="0 0 32 32"
    width={size}
    height={size}
    className={cn("shrink-0", className)}
    aria-hidden="true"
  >
    <circle cx="16" cy="16" r="15" fill="#050505" stroke="#fff" />
    <path
      fill="#ec1630"
      d="m16 4.5 2.7 7.6 8.3.3-6.6 5 2.3 8.1-6.7-4.7-6.7 4.7 2.3-8.1-6.6-5 8.3-.3Z"
    />
  </svg>
);

export const renderFusionWorldBrandMark: GameIconRenderer = (props) =>
  renderBrandImage(props, {
    src: "/img/gameIcons/fusionWorldLogo.png",
    intrinsicWidth: 510,
    intrinsicHeight: 272,
  });

export const renderFleshAndBloodGameIcon: GameIconRenderer = (props) =>
  renderBrandImage(props, {
    src: "/img/gameIcons/fleshAndBloodIcon.png",
    intrinsicWidth: 16,
    intrinsicHeight: 16,
  });

export const renderFleshAndBloodBrandMark: GameIconRenderer = (props) =>
  renderBrandImage(props, {
    src: "/img/gameIcons/fleshAndBloodLogo.png",
    intrinsicWidth: 687,
    intrinsicHeight: 350,
  });

export const renderGenericGameIcon: GameIconRenderer = ({
  size,
  color,
  className,
}) => (
  <Layers3 size={size} color={color} className={className} aria-hidden="true" />
);

export type SetIconSize = "base" | "sm" | "lg" | "xl" | "2xl" | "3xl" | "2x";

export interface SetIconRenderProps {
  setCode: string;
  size?: SetIconSize;
  className?: string;
  "aria-hidden"?: boolean;
  set?: ShortCardSet;
}

export type SetIconRenderer = (props: SetIconRenderProps) => ReactNode;

function buildKeyruneClass(
  setCode: string,
  keyruneCode: string | undefined,
  size?: "2x"
) {
  const keyrune =
    keyruneCode && keyruneCode !== "DEFAULT"
      ? keyruneCode.toLowerCase()
      : setCode.toLowerCase() || "DEFAULT";
  const sizeClass = size === "2x" ? "ss-2x" : "";
  return `ss ss-${keyrune} text-center w-fit ${sizeClass}`.trim();
}

export const renderKeyruneSetIcon: SetIconRenderer = ({
  setCode,
  size = "base",
  className,
  "aria-hidden": ariaHidden = true,
  set,
}) => {
  if (size === "2x") {
    return (
      <i
        className={cn(
          buildKeyruneClass(setCode, set?.keyruneCode, size),
          className
        )}
        aria-hidden={ariaHidden}
      />
    );
  }

  const textSizeClass =
    {
      sm: "text-sm",
      base: "text-2xl",
      lg: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
      "3xl": "text-3xl",
    }[size] || "text-lg";

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center",
        textSizeClass,
        className
      )}
    >
      <i
        className={buildKeyruneClass(setCode, set?.keyruneCode)}
        aria-hidden={ariaHidden}
      />
    </span>
  );
};

function renderCircularSetIcon(
  renderer: SetIconRenderer,
  props: SetIconRenderProps
) {
  return (
    <span
      className={cn(
        "inline-flex size-8 shrink-0 items-center justify-center rounded-full",
        props.className,
        "bg-secondary text-secondary-content"
      )}
    >
      {renderer({
        ...props,
        size: "base",
        className:
          "text-secondary-content inline-flex size-6 items-center justify-center leading-none",
      })}
    </span>
  );
}

export const renderKeyruneSetIconChip: SetIconRenderer = (props) =>
  renderCircularSetIcon(renderKeyruneSetIcon, props);

const codeSetIconMetrics: Record<
  SetIconSize,
  { maxFontSize: number; minFontSize: number; maxWidth: number }
> = {
  sm: { maxFontSize: 9, minFontSize: 5.5, maxWidth: 32 },
  base: { maxFontSize: 10, minFontSize: 5.5, maxWidth: 48 },
  lg: { maxFontSize: 11, minFontSize: 5.5, maxWidth: 56 },
  xl: { maxFontSize: 12, minFontSize: 5.5, maxWidth: 64 },
  "2xl": { maxFontSize: 12, minFontSize: 5.5, maxWidth: 72 },
  "3xl": { maxFontSize: 14, minFontSize: 5.5, maxWidth: 80 },
  "2x": { maxFontSize: 11, minFontSize: 5.5, maxWidth: 64 },
};

function fitSetCode(setCode: string, size: SetIconSize) {
  const text = setCode.trim().toUpperCase();
  const metrics = codeSetIconMetrics[size];
  // Reserve a conservative average width for proportional uppercase text so
  // wide characters still fit without switching the app to a code typeface.
  const fittedFontSize = metrics.maxWidth / Math.max(text.length * 0.68, 1);

  return {
    text,
    fontSize: Math.max(
      metrics.minFontSize,
      Math.min(metrics.maxFontSize, fittedFontSize)
    ),
    maxWidth: metrics.maxWidth,
  };
}

export const renderCodeSetIcon: SetIconRenderer = ({
  setCode,
  size = "base",
  className,
}) => {
  const fitted = fitSetCode(setCode, size);

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden whitespace-nowrap font-semibold leading-none tracking-tight",
        size === "2x" && "h-8 min-w-8 px-0.5",
        className
      )}
      style={{ fontSize: fitted.fontSize, maxWidth: fitted.maxWidth }}
      title={setCode}
    >
      {fitted.text}
    </span>
  );
};

export const renderOnePieceSetIcon: SetIconRenderer = ({ setCode }) => (
  <span className="text-sm leading-8">
    {getOnePieceDisplaySetCode(setCode)}
  </span>
);

const pokemonSizeMap: Record<SetIconSize, number> = {
  sm: 20,
  base: 24,
  lg: 18,
  xl: 20,
  "2xl": 24,
  "3xl": 30,
  "2x": 32,
};

export const renderPokemonSetIcon: SetIconRenderer = ({
  setCode,
  size = "base",
  className,
  "aria-hidden": ariaHidden = true,
}) => {
  const url = getPokemonSetIconUrl(setCode);
  if (!url) return <span className={cn("text-xs", className)}>{setCode}</span>;

  const px = pokemonSizeMap[size];
  const style: CSSProperties = {
    width: px,
    height: px,
    maskImage: `url(${url})`,
    maskPosition: "center",
    maskRepeat: "no-repeat",
    maskSize: "contain",
    WebkitMaskImage: `url(${url})`,
    WebkitMaskPosition: "center",
    WebkitMaskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
  };

  return (
    <span
      role={ariaHidden ? undefined : "img"}
      aria-label={ariaHidden ? undefined : setCode}
      aria-hidden={ariaHidden}
      className={cn("inline-block shrink-0 bg-current", className)}
      style={style}
    />
  );
};

export const renderPokemonSetIconChip: SetIconRenderer = (props) =>
  renderCircularSetIcon(renderPokemonSetIcon, props);
